import React, { createContext, useMemo } from "react";
import { Flow, Step, Task } from "@/types/flows";

// === CORE-APP CONTEXT ===
export type SetStep = (
    flow: Flow,
    step: Step | ((prev: Step) => Step),
    workspaceUrlOverride?: string | null,
    workflowUrlOverride?: string | null
) => void;

interface CoreAppState {
    task: Task;
    selectedObject: string | null;
    previouslySelectedObject: string | null;
    hideSidebar: boolean;
    showLabels: boolean;
    // loading: boolean;
}

interface CoreAppActions {
    setTask: React.Dispatch<React.SetStateAction<Task>>;
    setPreviouslySelectedObject: React.Dispatch<React.SetStateAction<string | null>>;
    setHideSidebar: React.Dispatch<React.SetStateAction<boolean>>;
    setShowLabels: React.Dispatch<React.SetStateAction<boolean>>;
    // setLoading: React.Dispatch<React.SetStateAction<boolean>>;
}

export interface CoreAppContextType extends CoreAppState, CoreAppActions {}

export const CoreAppContext = createContext<CoreAppContextType | undefined>(undefined);

// === HOOKS ===
export function useCoreApp(): CoreAppContextType {
    const context = React.useContext(CoreAppContext);
    if (!context) {
        throw new Error("useCoreApp must be used within a CoreAppProvider");
    }
    return context;
}

// A UI-focused subset of core state – avoids re-renders on unrelated changes.
export function useUIState() {
    const coreApp = useCoreApp();
    return useMemo(
        () => ({
            step: coreApp.step,
            setStep: coreApp.setStep,
            task: coreApp.task,
            setTask: coreApp.setTask,
            hideSidebar: coreApp.hideSidebar,
            setHideSidebar: coreApp.setHideSidebar,
            selectedObject: coreApp.selectedObject
        }),
        [
            coreApp.step,
            coreApp.setStep,
            coreApp.task,
            coreApp.setTask,
            coreApp.hideSidebar,
            coreApp.setHideSidebar,
            coreApp.selectedObject
        ]
    );
}
